import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff, ArrowRight, Building2, Lock, GraduationCap, BookOpen } from '@/components/DeskeyIcons';
import { getRememberedLoginEndpoint, rememberLoginEndpoint, forgetLoginEndpoint, postLoginPath } from '@/lib/loginEndpoint';
import MobileAppPromo from '@/components/MobileAppPromo';
import { notifyError } from '../lib/errors';
import { sendEmailCode, verifyEmailCode } from '@/lib/emailTwoFA';
import ResendCodeButton from '@/components/auth/ResendCodeButton';
import Loader from '../components/Loader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// An institution's own login page is for its teachers and students;
// institution admins sign in on the MyDeskey portal (backend enforces this).
const TABS = [
  { id: 'teacher', label: 'Teacher', icon: BookOpen },
  { id: 'student', label: 'Student', icon: GraduationCap },
];

const DISPLAY_FONT = { fontFamily: 'Space Grotesk, sans-serif' };
const DARK_INK = '#111827';
// Above this WCAG luminance, dark text has more contrast than white text.
const LIGHT_SURFACE_LUMINANCE = 0.179;

const FIELD = 'h-11 rounded-lg border-gray-200 bg-white text-sm shadow-none focus-visible:ring-2 focus-visible:ring-offset-0';
const LABEL = 'block text-sm font-medium text-gray-900';
const PRIMARY_BTN = 'w-full h-11 rounded-lg text-sm font-medium transition-[opacity,transform] hover:opacity-90 active:scale-[0.99]';
const QUIET_LINK = 'text-sm text-gray-600 hover:text-gray-900 transition-colors';

const postJson = async (path, body, token) => {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_URL}/api${path}`, { method: 'POST', headers, body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
};

// Error carrying the response, so friendlyError (notifyError) can use the
// server's detail — strings, validation lists, objects — or the status
// (e.g. 502 → "server unavailable") instead of raw text.
const requestError = (status, data, fallback) => Object.assign(new Error(fallback), { status, body: data });

/** WCAG relative luminance of a #rrggbb colour, 0 (black) to 1 (white). */
export function luminance(hex) {
  const c = (hex || '#000000').replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => {
    const v = parseInt(c.substr(i, 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

const isLight = (hex) => luminance(hex) > LIGHT_SURFACE_LUMINANCE;

function StepHeading({ title, children }) {
  return (
    <div className="mb-2">
      <h3 className="text-lg font-medium text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-600">{children}</p>
    </div>
  );
}

function BackToSignIn({ onClick }) {
  return <button type="button" onClick={onClick} className={`w-full text-center ${QUIET_LINK}`}>Back to sign in</button>;
}

/** The school's logo, or a building mark when it has none. */
function Crest({ logoUrl, name, large, tint, ink }) {
  const box = large ? 'w-16 h-16 rounded-2xl p-2' : 'w-10 h-10 rounded-xl p-1';
  if (logoUrl) {
    return <img src={logoUrl} alt={name} className={`${box} object-contain shrink-0`} style={{ background: tint }} data-testid={large ? 'tenant-logo' : undefined} />;
  }
  return (
    <div className={`${box} flex items-center justify-center shrink-0`} style={{ background: tint }}>
      <Building2 className={large ? 'w-7 h-7' : 'w-5 h-5'} style={{ color: ink }} />
    </div>
  );
}

function CenteredNotice({ testId, title, body, action, onAction }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6" data-testid={testId}>
      <div className="text-center max-w-sm">
        <Building2 className="w-10 h-10 text-gray-400 mx-auto mb-4" />
        <h1 className="text-xl font-medium text-gray-900">{title}</h1>
        <p className="mt-2 text-sm text-gray-600">{body}</p>
        <Button onClick={onAction} variant="outline" className="mt-6 rounded-lg">{action}</Button>
      </div>
    </div>
  );
}

export default function TenantLoginPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [unreachable, setUnreachable] = useState(false);
  // Preselect the tab this device last signed in with on this school's page.
  const [tab, setTab] = useState(() => {
    const saved = getRememberedLoginEndpoint();
    return saved && saved.slug === slug ? saved.role : 'teacher';
  });
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '' });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAMethod, setTwoFAMethod] = useState('email'); // email | totp
  const [preToken, setPreToken] = useState('');
  const [suggestTab, setSuggestTab] = useState(null);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/tenant/${slug}/config`)
      .then(r => {
        if (r.status === 404) return null;
        if (!r.ok) throw new Error(`config ${r.status}`);
        return r.json();
      })
      .then(d => {
        if (!d) {
          // Endpoint gone (e.g. the school renamed it) — stop sending this device here.
          forgetLoginEndpoint(slug);
          setNotFound(true);
        } else {
          setConfig(d);
          // A suspended school shows a notice instead of the sign-in form.
          if (d.status === 'suspended') setMode('suspended');
        }
        setLoadingConfig(false);
      })
      // Server down or network error: the school still exists, so keep it remembered.
      .catch(() => { setUnreachable(true); setLoadingConfig(false); });
  }, [slug]);

  const pc = config?.branding?.primary_color || '#34A853';
  const bg = config?.branding?.bg_color || '#0f172a';
  const logoUrl = config?.branding?.logo_url;
  const instName = config?.name || 'Institution';
  const loginTitle = config?.branding?.login_title || instName;
  const loginSub = config?.branding?.login_subtitle || 'Sign in to your classes, lessons and coursework';

  const dark = !isLight(bg);
  const txt = dark ? '#ffffff' : DARK_INK;
  const sub = dark ? 'rgba(255,255,255,0.75)' : '#374151';
  const tint = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';
  // Brand colour stays the button fill; the text on it picks whichever ink reads.
  const primaryStyle = { background: pc, color: isLight(pc) ? DARK_INK : '#ffffff' };
  const fieldStyle = { '--tw-ring-color': pc };
  const crest = { logoUrl, name: instName, tint, ink: txt };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const switchTab = (id) => { setTab(id); setMode('login'); setError(''); setSuggestTab(null); setSuccess(''); setForm({ email: '', password: '' }); setTwoFACode(''); setResetCode(''); setNewPassword(''); };
  // Wrong-tab hint: keep what the user typed, just move them to the right tab.
  const goToSuggestedTab = () => { setTab(suggestTab); setSuggestTab(null); setError(''); };
  const backToLogin = () => { setMode('login'); setError(''); setSuccess(''); setPreToken(''); };

  const handleLogin = async (e) => {
    e.preventDefault(); setError(''); setSuggestTab(null); setLoading(true);
    try {
      const { ok, status, data } = await postJson(`/tenant/${slug}/login`, { email: form.email, password: form.password, expected_role: tab });
      if (!ok) {
        if (data.detail?.code === 'wrong_tab') setSuggestTab(data.detail.expected_tab);
        throw requestError(status, data, 'Login failed');
      }
      if (data.needs_2fa && data.method === 'totp') {
        // TOTP accounts get a short-lived pre_token; the real JWT comes after the code.
        setPreToken(data.pre_token); setTwoFAMethod('totp'); setTwoFACode('');
        sessionStorage.setItem('pending_user', JSON.stringify(data.user || null));
        setMode('2fa');
      } else if (data.needs2FA) {
        // Email-code accounts also get only a pre_token; the real JWT comes from /2fa/verify.
        await sendEmailCode(data.pre_token);
        setPreToken(data.pre_token); setTwoFAMethod('email'); setTwoFACode('');
        sessionStorage.setItem('pending_user', JSON.stringify(data.user));
        sessionStorage.setItem('pending_tenant', JSON.stringify(data.tenant || null));
        setMode('2fa');
      } else { completeLogin(data.token, data.user, data.tenant); }
    } catch (err) { notifyError(err, 'Sign in failed. Please try again.'); } setLoading(false);
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      if (twoFAMethod === 'totp') {
        const { ok, status, data } = await postJson('/auth/2fa/verify-totp', { pre_token: preToken, code: twoFACode });
        if (!ok) throw requestError(status, data, 'Invalid code');
        const pendingUser = JSON.parse(sessionStorage.getItem('pending_user') || 'null');
        completeLogin(data.access_token, { ...(pendingUser || {}), ...(data.user || {}) }, null);
        return;
      }
      const data = await verifyEmailCode(preToken, twoFACode);
      completeLogin(data.access_token, JSON.parse(sessionStorage.getItem('pending_user')), JSON.parse(sessionStorage.getItem('pending_tenant') || 'null'));
    } catch (err) { notifyError(err, 'Could not verify the code.'); } setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault(); setError(''); setSuccess(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/password-reset/request`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: form.email }) });
      const data = await res.json(); if (!res.ok) throw new Error(data.detail || 'Failed');
      setSuccess('Code sent'); setMode('reset-confirm');
    } catch (err) { notifyError(err, 'Could not send the reset code.'); } setLoading(false);
  };

  const handleResetConfirm = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/password-reset/confirm`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: form.email, code: resetCode, new_password: newPassword }) });
      const data = await res.json(); if (!res.ok) throw new Error(data.detail || 'Failed');
      setSuccess(data.message); setTimeout(() => setMode('login'), 1500);
    } catch (err) { notifyError(err, 'Could not reset your password.'); } setLoading(false);
  };

  const completeLogin = (token, user, tenant) => {
    localStorage.setItem('desky_token', token);
    localStorage.setItem('desky_user', JSON.stringify(user));
    if (tenant) localStorage.setItem('desky_tenant', JSON.stringify(tenant));
    rememberLoginEndpoint(config?.slug || slug, user.role);
    ['pending_token', 'pending_user', 'pending_tenant'].forEach(k => sessionStorage.removeItem(k));
    // assign (not replace) keeps this login page in the browser history, so
    // Back from the dashboard returns to the school's own login page instead
    // of skipping past it to the MyDeskey portal.
    window.location.assign(postLoginPath(user));
  };

  // School colours aren't known yet, so wait on a neutral page instead of flashing navy.
  if (loadingConfig) return <div className="min-h-screen flex items-center justify-center bg-white"><Loader /></div>;

  if (unreachable) return (
    <CenteredNotice testId="tenant-unreachable" title="Can't connect right now" body="Please try again in a minute."
      action="Try again" onAction={() => window.location.reload()} />
  );

  if (notFound) return (
    <CenteredNotice testId="tenant-not-found" title="School page not found" body="Check the address, or ask your school for its sign-in link."
      action="Go to MyDeskey" onAction={() => nav('/mydeskey')} />
  );

  const feedback = (
    <>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {success && <p className="text-sm text-green-700">{success}</p>}
    </>
  );

  return (
    <div className="h-screen flex overflow-hidden" data-testid="tenant-login-page">

      {/* School panel: the school's colour and crest, nothing else competes */}
      <aside className="hidden lg:flex lg:w-[48%] xl:w-[45%] flex-col justify-center px-12 xl:px-16 py-12" style={{ background: bg }}>
        <div className="max-w-lg">
          <Crest {...crest} large />
          <h1 className="mt-8 text-5xl xl:text-6xl font-medium leading-[1.05] tracking-tight" style={{ ...DISPLAY_FONT, color: txt }}>
            {loginTitle}
          </h1>
          <p className="mt-4 max-w-md text-lg leading-relaxed" style={{ color: sub }}>{loginSub}</p>
        </div>
      </aside>

      <main className="flex-1 flex flex-col bg-white overflow-y-auto">

        {/* Below lg the school panel becomes this band */}
        <div className="lg:hidden flex items-center gap-4 px-6 py-6" style={{ background: bg }}>
          <Crest {...crest} />
          <div className="min-w-0">
            <h2 className="text-xl font-medium truncate" style={{ ...DISPLAY_FONT, color: txt }}>{loginTitle}</h2>
            <p className="mt-1 text-sm" style={{ color: sub }}>{loginSub}</p>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 sm:px-10 lg:px-16 py-12">
          <div className="w-full max-w-sm" data-testid="tenant-login-card">

            {(mode === 'login' || mode === 'forgot') && (
              <div className="flex gap-1 p-1 mb-8 rounded-lg bg-gray-100" role="tablist" data-testid="tenant-role-tabs">
                {TABS.map(t => {
                  const active = tab === t.id;
                  return (
                    <button key={t.id} type="button" role="tab" aria-selected={active} data-testid={`tenant-tab-${t.id}`} onClick={() => switchTab(t.id)}
                      className={`flex-1 h-9 rounded text-sm font-medium flex items-center justify-center gap-2 transition-colors ${active ? 'bg-white text-gray-900 shadow-sm ring-1 ring-black/5' : 'text-gray-600 hover:text-gray-900'}`}>
                      <t.icon className="w-4 h-4" /> {t.label}
                    </button>
                  );
                })}
              </div>
            )}

            {mode === 'suspended' && (
              <div role="alert" data-testid="tenant-suspended-notice">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 bg-red-50"><Lock className="w-5 h-5 text-red-600" /></div>
                <h3 className="text-lg font-medium text-gray-900">Account suspended</h3>
                <p className="mt-2 text-sm text-gray-600">{instName}&apos;s Deskey account is suspended, so sign-in is unavailable. Please contact your school administrator.</p>
              </div>
            )}

            {mode === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4" data-testid="tenant-login-form">
                <div>
                  <label htmlFor="tenant-email" className={`${LABEL} mb-2`}>Email</label>
                  <Input id="tenant-email" data-testid="tenant-login-email" type="email" autoComplete="username" value={form.email} onChange={e => f('email', e.target.value)} required className={FIELD} style={fieldStyle} />
                </div>
                <div>
                  <div className="flex items-baseline justify-between mb-2">
                    <label htmlFor="tenant-password" className={LABEL}>Password</label>
                    <button type="button" data-testid="tenant-forgot-btn" onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }} className={QUIET_LINK}>Forgot password?</button>
                  </div>
                  <div className="relative">
                    <Input id="tenant-password" data-testid="tenant-login-password" type={show ? 'text' : 'password'} autoComplete="current-password" value={form.password} onChange={e => f('password', e.target.value)} required className={`${FIELD} pr-10`} style={fieldStyle} />
                    <button type="button" onClick={() => setShow(!show)} aria-label={show ? 'Hide password' : 'Show password'} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-500 hover:text-gray-900 transition-colors">
                      {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {error && <p className="text-sm text-red-600" data-testid="tenant-login-error">{error}</p>}
                {suggestTab && (
                  <button type="button" onClick={goToSuggestedTab} className="text-sm font-medium text-gray-900 underline underline-offset-4" data-testid="tenant-switch-tab">
                    Switch to {TABS.find(t => t.id === suggestTab)?.label} tab
                  </button>
                )}
                <Button type="submit" data-testid="tenant-login-submit" disabled={loading} className={PRIMARY_BTN} style={primaryStyle}>
                  {loading ? 'Signing in…' : 'Sign in'} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </form>
            )}
            {mode === 'login' && <MobileAppPromo role={tab} />}

            {mode === '2fa' && (
              <form onSubmit={handleVerify2FA} className="space-y-4" data-testid="tenant-2fa-form">
                <StepHeading title="Enter your code">
                  {twoFAMethod === 'totp' ? 'Use the 6-digit code from your authenticator app' : `We sent a 6-digit code to ${form.email}`}
                </StepHeading>
                <Input data-testid="tenant-2fa-code" inputMode="numeric" autoComplete="one-time-code" aria-label="6-digit code" placeholder="000000" value={twoFACode} onChange={e => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))} required maxLength={6} className={`${FIELD} h-12 text-center text-xl tracking-[0.5em] font-mono tabular-nums`} style={fieldStyle} />
                {feedback}
                <Button type="submit" disabled={loading || twoFACode.length !== 6} className={PRIMARY_BTN} style={primaryStyle} data-testid="tenant-2fa-submit">{loading ? 'Verifying…' : 'Verify and sign in'}</Button>
                {twoFAMethod === 'email' && (
                  <ResendCodeButton onResend={async () => { await sendEmailCode(preToken); setTwoFACode(''); }} className="text-gray-600 hover:text-gray-900" />
                )}
                <BackToSignIn onClick={backToLogin} />
              </form>
            )}

            {mode === 'forgot' && (
              <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="tenant-forgot-form">
                <StepHeading title="Reset your password">We&apos;ll email you a 6-digit reset code</StepHeading>
                <Input data-testid="tenant-forgot-email" type="email" autoComplete="username" aria-label="Email" placeholder="Email" value={form.email} onChange={e => f('email', e.target.value)} required className={FIELD} style={fieldStyle} />
                {feedback}
                <Button type="submit" data-testid="tenant-forgot-submit" disabled={loading} className={PRIMARY_BTN} style={primaryStyle}>{loading ? 'Sending…' : 'Send code'}</Button>
                <BackToSignIn onClick={backToLogin} />
              </form>
            )}

            {mode === 'reset-confirm' && (
              <form onSubmit={handleResetConfirm} className="space-y-4" data-testid="tenant-reset-form">
                <StepHeading title="Set a new password">Enter the code we sent to {form.email}</StepHeading>
                <Input data-testid="tenant-reset-code" inputMode="numeric" autoComplete="one-time-code" aria-label="6-digit code" placeholder="6-digit code" value={resetCode} onChange={e => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))} required maxLength={6} className={`${FIELD} text-center tracking-[0.3em] font-mono tabular-nums`} style={fieldStyle} />
                <Input data-testid="tenant-reset-password" type="password" autoComplete="new-password" aria-label="New password" placeholder="New password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required className={FIELD} style={fieldStyle} />
                {feedback}
                <Button type="submit" data-testid="tenant-reset-submit" disabled={loading} className={PRIMARY_BTN} style={primaryStyle}>{loading ? 'Saving…' : 'Save new password'}</Button>
                <BackToSignIn onClick={backToLogin} />
              </form>
            )}
          </div>
        </div>

        <footer className="flex items-center justify-center gap-2 px-6 py-4 border-t border-gray-100 text-xs text-gray-500">
          <svg width="14" height="14" viewBox="0 0 40 40" fill="none" aria-hidden="true"><path d="M11.5 7.2C11.5 4.1 15 2.2 17.6 3.9L34.2 15.7C36.5 17.3 36.5 20.7 34.2 22.3L17.6 34.1C15 35.8 11.5 33.9 11.5 30.8V7.2Z" fill="#34A853"/></svg>
          <span>Powered by</span>
          <span className="font-medium text-gray-700" style={DISPLAY_FONT}>Deskey AI</span>
        </footer>
      </main>
    </div>
  );
}
